import { LANGUAGES } from './languages';

export type Language = 'en' | 'tr' | 'es' | 'de' | 'zh' | 'ja' | 'ko';

type LanguageNamespace = 'labels' | 'common' | 'table_columns' | 'directions' | 'app' | 'surveys';

// Map language codes to their full names
const LANGUAGE_CODE_TO_NAME: Record<Language, string> = {
  'en': 'English',
  'tr': 'Türkçe',
  'es': 'Español',
  'de': 'Deutsch',
  'zh': '中文',
  'ja': '日本語',
  'ko': 'Korean'
};

// Type guard to check if a key is a valid namespace
const isLanguageNamespace = (key: string): key is LanguageNamespace => {
  return ['labels', 'common', 'table_columns', 'directions', 'app', 'surveys'].includes(key);
};

export const useTranslation = (lang: Language = 'en') => {
  const t = (key: string, namespace?: string): string => {
    const langName = LANGUAGE_CODE_TO_NAME[lang] || 'English';
    const langData = LANGUAGES[langName as keyof typeof LANGUAGES];
    
    if (!langData) return key;

    // First, try to get the value directly from the root level
    if (key.includes('.')) {
      const parts = key.split('.');
      let current: any = langData;
      
      for (const part of parts) {
        if (current && typeof current === 'object' && part in current) {
          current = current[part];
        } else {
          current = undefined;
          break;
        }
      }
      
      if (current !== undefined && typeof current === 'string') {
        return current;
      }
    }
    
    // If namespace is provided and valid, try that
    if (namespace && isLanguageNamespace(namespace)) {
      const nsData = langData[namespace];
      if (nsData && key in nsData) {
        return nsData[key as keyof typeof nsData];
      }
    }
    
    // Otherwise try all namespaces in order
    const namespaces: LanguageNamespace[] = ['app', 'labels', 'common', 'table_columns', 'directions', 'surveys'];
    for (const ns of namespaces) {
      const nsData = langData[ns];
      if (nsData && key in nsData) {
        return nsData[key as keyof typeof nsData];
      }
    }
    
    // Fallback to key if not found
    return key;
  };

  return { t, lang };
};

export const langManager = {
  getTransportModeDisplay: (mode: string) => mode,
  getMaterialDisplay: (material: string) => material,
  getAvailableLanguages: (): {code: Language, name: string}[] => {
    return Object.entries(LANGUAGE_CODE_TO_NAME).map(([code, name]) => ({
      code: code as Language,
      name
    }));
  }
};